import { useState, useCallback } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * PDF export hook - DOM elementini PDF'e dönüştürür
 * 
 * @example
 * const tableRef = useRef(null);
 * const { exportToPDF, isExporting } = useExportPDF();
 * 
 * exportToPDF({
 *   elementRef: tableRef,
 *   fileName: 'musteri_detay',
 *   title: 'Müşteri Raporu',
 *   orientation: 'portrait',
 * });
 */
export default function useExportPDF() {
    const [isExporting, setIsExporting] = useState(false);

    /**
     * PDF'e export et
     */
    const exportToPDF = useCallback(async ({
        elementRef,
        element,
        fileName = 'rapor',
        title = null,
        orientation = 'portrait',
        includeDate = true,
        format = 'a4',
        margin = 10,
        quality = 2,
    }) => {
        const targetElement = element || elementRef?.current;

        if (!targetElement) {
            console.warn('Export için element bulunamadı');
            return false;
        }

        setIsExporting(true);

        try {
            // Elementi klonla ve CSS'leri temizle (oklab/oklch desteği için)
            const cloneForCanvas = targetElement.cloneNode(true);

            // HTML'i canvas'a çevir
            const canvas = await html2canvas(targetElement, {
                scale: quality,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
                windowWidth: targetElement.scrollWidth,
                windowHeight: targetElement.scrollHeight,
                // Sorunlu elementleri atla
                ignoreElements: (element) => {
                    // SVG gradient'leri ve bazı sorunlu elementleri atla
                    if (element.tagName === 'STYLE') return true;
                    return false;
                },
                // Modern CSS renk fonksiyonlarını temizle
                onclone: (clonedDoc, element) => {
                    // Tüm style etiketlerini ve inline stilleri temizle
                    const allElements = clonedDoc.querySelectorAll('*');
                    allElements.forEach(el => {
                        try {
                            // Computed style sorunlu olabilir, direkt style override yap
                            if (el.style) {
                                // Oklab/oklch içeren stilleri sıfırla
                                const inlineStyle = el.getAttribute('style') || '';
                                if (inlineStyle.includes('oklab') || inlineStyle.includes('oklch')) {
                                    el.removeAttribute('style');
                                }
                            }
                        } catch (e) {
                            // Ignore
                        }
                    });
                },
            }).catch(err => {
                console.warn('html2canvas hatası, basit mod deneniyor:', err.message);
                // Fallback: basit mod
                return html2canvas(targetElement, {
                    scale: 1,
                    useCORS: true,
                    logging: false,
                    backgroundColor: '#ffffff',
                    foreignObjectRendering: false,
                });
            });

            const imgData = canvas.toDataURL('image/png');
            const imgWidth = canvas.width;
            const imgHeight = canvas.height;

            // PDF boyutlarını hesapla (mm cinsinden)
            const pdf = new jsPDF({
                orientation,
                unit: 'mm',
                format,
            });

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const availableWidth = pdfWidth - (margin * 2);

            // Başlık varsa ekle
            let yOffset = margin;
            if (title) {
                pdf.setFontSize(16);
                pdf.setFont(undefined, 'bold');
                pdf.text(title, margin, yOffset + 5);
                yOffset += 15;

                // Tarih ekle
                if (includeDate) {
                    pdf.setFontSize(10);
                    pdf.setFont(undefined, 'normal');
                    const dateStr = new Date().toLocaleDateString('tr-TR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                    });
                    pdf.text(`Oluşturulma: ${dateStr}`, margin, yOffset);
                    yOffset += 10;
                }
            }

            // Resmi ölçekle
            const ratio = availableWidth / (imgWidth / quality);
            const scaledWidth = availableWidth;
            const scaledHeight = (imgHeight / quality) * ratio;
            const availableHeight = pdfHeight - yOffset - margin;

            // Tek sayfa mı yoksa çok sayfa mı?
            if (scaledHeight <= availableHeight) {
                // Tek sayfa
                pdf.addImage(imgData, 'PNG', margin, yOffset, scaledWidth, scaledHeight);
            } else {
                // Çok sayfa - sayfaları böl
                let remainingHeight = scaledHeight;
                let currentY = 0;
                let pageNum = 0;

                while (remainingHeight > 0) {
                    if (pageNum > 0) {
                        pdf.addPage();
                        yOffset = margin;
                    }

                    const pageHeight = Math.min(remainingHeight, availableHeight);

                    // Canvas'tan ilgili kısmı al
                    const sourceY = (currentY / ratio) * quality;
                    const sourceHeight = (pageHeight / ratio) * quality;

                    // Yeni canvas oluştur (sayfa için)
                    const pageCanvas = document.createElement('canvas');
                    pageCanvas.width = imgWidth;
                    pageCanvas.height = sourceHeight;
                    const ctx = pageCanvas.getContext('2d');
                    ctx.drawImage(
                        canvas,
                        0, sourceY,
                        imgWidth, sourceHeight,
                        0, 0,
                        imgWidth, sourceHeight
                    );

                    const pageImgData = pageCanvas.toDataURL('image/png');
                    pdf.addImage(pageImgData, 'PNG', margin, yOffset, scaledWidth, pageHeight);

                    remainingHeight -= pageHeight;
                    currentY += pageHeight;
                    pageNum++;
                }
            }

            // Dosya adını formatla
            const dateStr = includeDate
                ? `_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}`
                : '';
            const fullFileName = `${fileName}${dateStr}.pdf`;

            // PDF'i indir
            pdf.save(fullFileName);

            return true;
        } catch (error) {
            console.error('PDF export hatası:', error);
            return false;
        } finally {
            setIsExporting(false);
        }
    }, []);

    /**
     * Birden fazla elementi tek PDF'te birleştir
     */
    const exportMultipleToPDF = useCallback(async ({
        elements,
        fileName = 'rapor',
        title = null,
        orientation = 'portrait',
        includeDate = true,
    }) => {
        if (!elements || elements.length === 0) {
            console.warn('Export için element bulunamadı');
            return false;
        }

        setIsExporting(true);

        try {
            const pdf = new jsPDF({
                orientation,
                unit: 'mm',
                format: 'a4',
            });

            const margin = 10;
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const availableWidth = pdfWidth - (margin * 2);

            for (let i = 0; i < elements.length; i++) {
                if (i > 0) pdf.addPage();

                const el = elements[i];
                const canvas = await html2canvas(el, {
                    scale: 2,
                    useCORS: true,
                    logging: false,
                    backgroundColor: '#ffffff',
                });

                const imgData = canvas.toDataURL('image/png');
                const ratio = availableWidth / (canvas.width / 2);
                const scaledHeight = (canvas.height / 2) * ratio;

                pdf.addImage(imgData, 'PNG', margin, margin, availableWidth, scaledHeight);
            }

            const dateStr = includeDate
                ? `_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}`
                : '';
            pdf.save(`${fileName}${dateStr}.pdf`);

            return true;
        } catch (error) {
            console.error('PDF export hatası:', error);
            return false;
        } finally {
            setIsExporting(false);
        }
    }, []);

    return {
        exportToPDF,
        exportMultipleToPDF,
        isExporting,
    };
}
